import React, { useEffect, useState } from "react";
import api from "../api";

const emptyForm = {
  id: null,
  nome: "",
  descricao: "",
  slots_inclusos: "",
  valor_base: "",
  valor_slot_extra: "",
  valor_base_usd: "",
  valor_slot_extra_usd: "",
  ativo: 1,
};

export default function PlanosAdmin() {
  const [planos, setPlanos] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isEditing = !!form.id;

  const carregarPlanos = async () => {
    try {
      setLoading(true);
      setError("");
      const resp = await api.get("/api/planos");
      setPlanos(resp.data || []);
    } catch (err) {
      console.error("Erro ao carregar planos:", err);
      setError("Erro ao carregar planos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarPlanos();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEdit = (plano) => {
    setForm({
      id: plano.id,
      nome: plano.nome || "",
      descricao: plano.descricao || "",
      slots_inclusos: plano.slots_inclusos?.toString() || "",
      valor_base: plano.valor_base?.toString() || "",
      valor_slot_extra: plano.valor_slot_extra?.toString() || "",
      valor_base_usd: plano.valor_base_usd?.toString() || "",
      valor_slot_extra_usd: plano.valor_slot_extra_usd?.toString() || "",
      ativo: plano.ativo,
    });
  };

  const handleNovo = () => {
    setForm(emptyForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      const payload = {
        ...form,
        slots_inclusos: form.slots_inclusos || 0,
        valor_base: form.valor_base || 0,
        valor_slot_extra: form.valor_slot_extra || 0,
        valor_base_usd: form.valor_base_usd || 0,
        valor_slot_extra_usd: form.valor_slot_extra_usd || 0,
        ativo: Number(form.ativo) || 0,
      };

      if (isEditing) {
        await api.put(`/api/planos/${form.id}`, payload);
      } else {
        await api.post("/api/planos", payload);
      }

      await carregarPlanos();
      setForm(emptyForm);
    } catch (err) {
      console.error("Erro ao salvar plano:", err);
      setError("Erro ao salvar plano.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAtivo = async (plano) => {
    try {
      const novoAtivo = plano.ativo === 1 ? 0 : 1;
      await api.put(`/api/planos/${plano.id}`, {
        ...plano,
        ativo: novoAtivo,
      });
      await carregarPlanos();
    } catch (err) {
      console.error("Erro ao alterar status do plano:", err);
      setError("Erro ao alterar status do plano.");
    }
  };

  const handleDesativarHard = async (plano) => {
    if (!window.confirm(`Desativar o plano "${plano.nome}"?`)) return;

    try {
      await api.delete(`/api/planos/${plano.id}`);
      await carregarPlanos();
    } catch (err) {
      console.error("Erro ao desativar plano:", err);
      setError("Erro ao desativar plano.");
    }
  };

  const formatMoneyBRL = (v) =>
    `R$ ${Number(v || 0).toFixed(2)}`;

  const formatMoneyUSD = (v) =>
    `US$ ${Number(v || 0).toFixed(2)}`;

  return (
    <div className="container mt-4">
      <h2>Gerenciamento de Planos</h2>

      {error && <div className="alert alert-danger mt-2">{error}</div>}

      {/* Tabela de planos */}
      <div className="card mt-3">
        <div className="card-body">
          <h5 className="card-title">Planos cadastrados</h5>
          {loading ? (
            <p>Carregando planos...</p>
          ) : planos.length === 0 ? (
            <p>Nenhum plano cadastrado.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-striped align-middle">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Nome</th>
                    <th>Contas incluídas</th>
                    <th>Base BRL</th>
                    <th>Conta extra BRL</th>
                    <th>Base USD</th>
                    <th>Conta extra USD</th>
                    <th>Ativo</th>
                    <th style={{ width: "1%", whiteSpace: "nowrap" }}>
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {planos.map((p) => (
                    <tr key={p.id}>
                      <td>{p.id}</td>
                      <td>{p.nome}</td>
                      <td>{p.slots_inclusos}</td>
                      <td>{formatMoneyBRL(p.valor_base)}</td>
                      <td>{formatMoneyBRL(p.valor_slot_extra)}</td>
                      <td>{formatMoneyUSD(p.valor_base_usd)}</td>
                      <td>{formatMoneyUSD(p.valor_slot_extra_usd)}</td>
                      <td>{p.ativo === 1 ? "Sim" : "Não"}</td>
                      <td>
                        <div className="btn-group btn-group-sm" role="group">
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => handleEdit(p)}
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => handleToggleAtivo(p)}
                          >
                            {p.ativo === 1 ? "Desativar" : "Reativar"}
                          </button>
                          <button
                            type="button"
                            className="btn btn-danger"
                            onClick={() => handleDesativarHard(p)}
                          >
                            Off
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Formulário de criação/edição */}
      <div className="card mt-4">
        <div className="card-body">
          <h5 className="card-title">
            {isEditing ? "Editar plano" : "Novo plano"}
          </h5>

          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Nome</label>
                <input
                  type="text"
                  className="form-control"
                  name="nome"
                  value={form.nome}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Contas incluídas</label>
                <input
                  type="number"
                  className="form-control"
                  name="slots_inclusos"
                  value={form.slots_inclusos}
                  onChange={handleChange}
                  min="0"
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Ativo</label>
                <select
                  className="form-select"
                  name="ativo"
                  value={form.ativo}
                  onChange={handleChange}
                >
                  <option value={1}>Sim</option>
                  <option value={0}>Não</option>
                </select>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Descrição (opcional)</label>
              <textarea
                className="form-control"
                name="descricao"
                value={form.descricao}
                onChange={handleChange}
                rows={3}
              />
            </div>

            <div className="row">
              <div className="col-md-3 mb-3">
                <label className="form-label">Valor base (BRL)</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  name="valor_base"
                  value={form.valor_base}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Conta extra (BRL)</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  name="valor_slot_extra"
                  value={form.valor_slot_extra}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Valor base (USD)</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  name="valor_base_usd"
                  value={form.valor_base_usd}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Conta extra (USD)</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  name="valor_slot_extra_usd"
                  value={form.valor_slot_extra_usd}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="mt-3 d-flex gap-2">
              <button
                type="submit"
                className="btn btn-success btn-sm"
                disabled={saving}
              >
                {saving
                  ? isEditing
                    ? "Salvando..."
                    : "Criando..."
                  : isEditing
                  ? "Salvar alterações"
                  : "Criar plano"}
              </button>

              {isEditing && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleNovo}
                >
                  Cancelar edição
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
